import store from '@/store'
import { N8N_TEAMS_URL } from '@/api/n8n'

export interface SLAProfile {
  id: string
  name: string
  firstResponseTime: number
  resolutionTime: number
  clientIdleTime: number
  redirectTime: number
  redirectAgents: { id: number; name: string }[]
  notifyBefore: number
}

export type SLAFluxoMap = Record<string, string> // fluxoId → profileId

export interface RemoteProfile {
  id: string
  name: string
  first_response_time: number
  resolution_time: number
  client_idle_time: number
  redirect_time: number
  notify_before: number
  redirect_agents: { agent_id: number; agent_name: string }[] | string | null
  fluxo_ids: number[] | string | null
}

function token(): string {
  return store.getters.authUser?.access_token || ''
}

async function post(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command, timestamp: Date.now(), access_token: token(), ...extra }),
  })
  if (!res.ok) throw new Error(`SLA API error ${res.status}`)
  // Sem nenhum perfil de SLA cadastrado, o n8n devolve corpo vazio em vez de
  // "[]" — res.json() quebra nesse caso ("Unexpected end of JSON input"),
  // mesmo bug já corrigido em useAdminDB.ts.
  const text = await res.text()
  if (!text.trim()) return []
  return JSON.parse(text)
}

export function parseJsonField<T>(v: T[] | string | null | undefined): T[] {
  if (Array.isArray(v)) return v
  if (typeof v === 'string' && v) try { return JSON.parse(v) } catch { /* */ }
  return []
}

export function fromRemote(r: RemoteProfile): SLAProfile {
  return {
    id:                r.id,
    name:              r.name,
    firstResponseTime: r.first_response_time,
    resolutionTime:    r.resolution_time,
    clientIdleTime:    r.client_idle_time,
    redirectTime:      r.redirect_time,
    notifyBefore:      r.notify_before,
    redirectAgents:    parseJsonField(r.redirect_agents as { agent_id: number; agent_name: string }[] | string | null)
                         .map(a => ({ id: a.agent_id, name: a.agent_name || '' })),
  }
}

export function toPayload(p: SLAProfile) {
  return {
    id:                   p.id,
    name:                 p.name,
    first_response_time:  p.firstResponseTime,
    resolution_time:      p.resolutionTime,
    client_idle_time:     p.clientIdleTime,
    redirect_time:        p.redirectTime,
    notify_before:        p.notifyBefore,
    redirect_agents:      p.redirectAgents.map(a => ({ agent_id: a.id, agent_name: a.name })),
  }
}

export async function fetchSLAProfiles(): Promise<{ profiles: SLAProfile[]; fluxoMap: SLAFluxoMap }> {
  const data = await post('get_slas') as unknown

  // Lida com 3 formatos de resposta do n8n:
  // [{profiles:[...]}] → Code node (legado)
  // {profiles:[...]}   → objeto direto
  // [row1, row2, ...]  → MySQL node (cada row é um perfil)
  let list: RemoteProfile[] = []
  if (Array.isArray(data)) {
    const first = (data as unknown[])[0] as Record<string, unknown> | undefined
    if (first && Array.isArray(first.profiles)) {
      list = first.profiles as RemoteProfile[]
    } else {
      list = data as unknown as RemoteProfile[]
    }
  } else if (data && typeof data === 'object') {
    list = ((data as Record<string, unknown>).profiles as RemoteProfile[]) ?? []
  }

  const profiles = list.map(fromRemote)

  const fluxoMap: SLAFluxoMap = {}
  list.forEach(r => {
    parseJsonField(r.fluxo_ids as number[] | string | null).forEach(fid => { fluxoMap[String(fid)] = r.id })
  })

  return { profiles, fluxoMap }
}

export async function createOrUpdateSLAProfile(p: SLAProfile, isNew: boolean): Promise<void> {
  await post(isNew ? 'create_sla' : 'update_sla', { profile: toPayload(p) })
}

export async function deleteSLAProfile(id: string): Promise<void> {
  await post('delete_sla', { id })
}

export async function setSLAFluxoProfile(fluxoId: string, profileId: string | null): Promise<void> {
  await post('set_sla_fluxo', { fluxo_id: Number(fluxoId), sla_profile_id: profileId })
}

export interface SLANotification {
  type: 'breached' | 'atRisk'
  conv_id: number
  contact_name: string
  contact_phone: string
  profile_name: string
  fluxo_name: string
  agent_name: string
  time_label: string
}

/** Dispara notificação de risco/violação de SLA. Fire-and-forget, como o chamador original. */
export function notifySLA(notification: SLANotification): void {
  post('notify', { notification }).catch(() => { /* silencioso */ })
}
